import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

function extractOrderNumber(displayName: string | null | undefined): number {
  if (!displayName) return 0;
  const m = displayName.match(/#(\d+)/);
  return m ? parseInt(m[1], 10) : 0;
}
import { NgxSpinnerService } from 'ngx-spinner';
import Swal from 'sweetalert2';
import {
  BillingProcess,
  BillingProcessItem,
} from 'src/app/models/billing-process';
import { BillingProcessService } from 'src/app/services/billing-process.service';
import { MessageUtil } from 'src/app/utils/message.util';

@Component({
  selector: 'app-wizard-step-existing-orders',
  templateUrl: './wizard-step-existing-orders.component.html',
  styleUrls: ['./wizard-step-existing-orders.component.scss'],
})
export class WizardStepExistingOrdersComponent implements OnInit {
  @Input() process: BillingProcess;
  @Output() next = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();

  allItems: BillingProcessItem[] = [];
  loading = false;
  personalizeOpen = false;
  showOnlyEligible = true;
  selectedOriginFilter: string[] = [];

  constructor(
    private billingProcessService: BillingProcessService,
    private spinner: NgxSpinnerService,
    private messageService: MessageUtil,
  ) {}

  ngOnInit(): void {
    this.loadItems(true);
  }

  private loadItems(autoLoadIfEmpty: boolean): void {
    this.loading = true;
    this.spinner.show();
    this.billingProcessService.listItems(this.process.id).subscribe({
      next: (items) => {
        const all = items ?? [];
        const hasAny = all.length > 0;
        if (autoLoadIfEmpty && !hasAny) {
          this.reload(true);
          return;
        }
        this.allItems = all;
        this.spinner.hide();
        this.loading = false;
      },
      error: (err) => {
        this.spinner.hide();
        this.loading = false;
        this.messageService.generateMessage(
          'error', 'SUMMARY.ERROR',
          err?.error?.message ?? 'Erro ao carregar pedidos existentes.');
      },
    });
  }

  reload(skipConfirm: boolean = false): void {
    if (!skipConfirm) {
      Swal.fire({
        title: 'Recarregar pedidos existentes',
        text: 'Esta ação apaga os items carregados e gera a lista novamente '
          + 'a partir dos pedidos abertos atuais. Deseja continuar?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sim, recarregar',
        cancelButtonText: 'Cancelar',
        reverseButtons: true,
      }).then((r) => { if (r.value) this.executeReload(); });
      return;
    }
    this.executeReload();
  }

  private executeReload(): void {
    this.loading = true;
    this.spinner.show();
    this.billingProcessService.loadEligibleItems(this.process.id).subscribe({
      next: (items) => {
        this.allItems = items ?? [];
        this.spinner.hide();
        this.loading = false;
      },
      error: (err) => {
        this.spinner.hide();
        this.loading = false;
        this.messageService.generateMessage(
          'error', 'SUMMARY.ERROR',
          err?.error?.message ?? 'Erro ao recarregar pedidos.');
      },
    });
  }

  toggle(item: BillingProcessItem): void {
    if (this.isLocked(item)) return;
    const newSelected = !item.selected;
    this.billingProcessService
      .updateItemSelection(this.process.id, item.id, newSelected)
      .subscribe({
        next: (updated) => { item.selected = updated.selected; },
        error: (err) => {
          this.messageService.generateMessage(
            'error', 'SUMMARY.ERROR',
            err?.error?.message ?? 'Erro ao atualizar seleção.');
        },
      });
  }

  toggleAll(select: boolean): void {
    const targets = this.items.filter((i) => !this.isLocked(i) && i.selected !== select);
    targets.forEach((item) => {
      this.billingProcessService
        .updateItemSelection(this.process.id, item.id, select)
        .subscribe({
          next: (updated) => { item.selected = updated.selected; },
          error: (err) => {
            this.messageService.generateMessage(
              'error', 'SUMMARY.ERROR',
              err?.error?.message ?? 'Erro ao atualizar seleção.');
          },
        });
    });
  }

  openPersonalize(): void {
    if (this.items.length === 0) return;
    this.showOnlyEligible = true;
    this.selectedOriginFilter = [];
    this.personalizeOpen = true;
  }

  orderNumberOf(item: BillingProcessItem): number | null {
    return extractOrderNumber(item.displayName) || null;
  }

  get originOptions(): Array<{ label: string; value: string }> {
    const set = new Set<string>();
    for (const i of this.items) {
      if (i.productName) set.add(i.productName);
    }
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
      .map((v) => ({ label: v, value: v }));
  }

  closePersonalize(): void { this.personalizeOpen = false; }

  isLocked(item: BillingProcessItem): boolean {
    return !!item.alreadyGenerated || !!item.emitterBlocked
      || item.executionStatus === 'SKIPPED';
  }

  customerSummary(item: BillingProcessItem): string {
    if (!item.customers || item.customers.length === 0) return '';
    return item.customers.map((c) => c.customerName || `#${c.customerId}`).join(', ');
  }

  customerTooltip(item: BillingProcessItem): string {
    if (!item.customers || item.customers.length === 0) return '';
    return item.customers
      .map((c) => {
        const name = c.customerName || `#${c.customerId}`;
        const quota = c.quota != null ? ` (${Number(c.quota).toFixed(0)}%)` : '';
        return `${name}${quota}`;
      })
      .join(' · ');
  }

  get items(): BillingProcessItem[] {
    return this.allItems
      .filter((i) => i.sourceType === 'EXISTING_ORDER')
      .sort((a, b) => {
        const an = extractOrderNumber(a.displayName);
        const bn = extractOrderNumber(b.displayName);
        return bn - an || (a.boatName || '').localeCompare(b.boatName || '', 'pt-BR');
      });
  }

  get available(): BillingProcessItem[] {
    return this.items.filter((i) => !this.isLocked(i));
  }

  get personalizeItems(): BillingProcessItem[] {
    let list = this.showOnlyEligible
      ? this.items.filter((i) => !this.isLocked(i))
      : this.items.slice();
    if (this.selectedOriginFilter.length > 0) {
      list = list.filter((i) =>
        i.productName != null && this.selectedOriginFilter.includes(i.productName));
    }
    return list;
  }

  get countAvailable(): number { return this.available.length; }
  get countSelected(): number {
    return this.available.filter((i) => i.selected).length;
  }
  get totalAvailable(): number {
    return this.available.reduce((s, i) => s + (Number(i.computedTotal) || 0), 0);
  }
  get totalSelected(): number {
    return this.available
      .filter((i) => i.selected)
      .reduce((s, i) => s + (Number(i.computedTotal) || 0), 0);
  }
}
